/**
 * AIDIA notifications — schedules reminder alerts that can fire when the app is
 * closed, using the Notification Triggers API (TimestampTrigger) when available.
 *
 * Scheduling happens while the vault is unlocked (we have the entry titles then).
 * The browser holds the scheduled notification and fires it at the due time even
 * if AIDIA/the browser is closed. Where triggers aren't supported, the in-app
 * checker in app.js still fires alerts on open — this degrades gracefully.
 */
const _mod = (() => {
  const TAG_PREFIX = "aidia-reminder-";
  const FIRE_HOUR = 9; // reminders fire at 09:00 local on the alert date
  let registration = null;

  const supportsTriggers = () =>
    "Notification" in window && "showTrigger" in Notification.prototype && "TimestampTrigger" in window;

  const permission = () => (("Notification" in window) ? Notification.permission : "denied");

  async function init() {
    if (!("serviceWorker" in navigator)) return;
    try {
      registration = await navigator.serviceWorker.register("sw.js");
      await navigator.serviceWorker.ready;
    } catch (e) {
      console.warn("Service worker registration failed", e);
    }
  }

  async function requestPermission() {
    if (!("Notification" in window)) return "denied";
    if (Notification.permission === "default") {
      try { return await Notification.requestPermission(); } catch { return "denied"; }
    }
    return Notification.permission;
  }

  function fireTimestamp(alertDate) {
    const d = new Date(alertDate + "T00:00:00");
    d.setHours(FIRE_HOUR, 0, 0, 0);
    return d.getTime();
  }

  async function getReg() {
    if (registration) return registration;
    if ("serviceWorker" in navigator) registration = await navigator.serviceWorker.ready;
    return registration;
  }

  // Cancel any pending scheduled notifications for a given entry id.
  async function cancelForEntry(id) {
    const reg = await getReg();
    if (!reg || !reg.getNotifications) return;
    try {
      const pending = await reg.getNotifications({ tag: TAG_PREFIX + id, includeTriggered: true });
      pending.forEach((n) => n.close());
    } catch {}
  }

  async function scheduleForEntry(entry) {
    if (!supportsTriggers() || permission() !== "granted") return;
    await cancelForEntry(entry.id);
    if (!entry.alertEnabled || !entry.alertDate) return;
    const ts = fireTimestamp(entry.alertDate);
    if (ts <= Date.now()) return; // past due — handled by the in-app checker
    const reg = await getReg();
    if (!reg) return;
    try {
      await reg.showNotification("🔔 AIDIA reminder: " + (entry.title || "Reminder"), {
        tag: TAG_PREFIX + entry.id,
        body: (entry.content || "You have a reminder due today.").slice(0, 120),
        icon: "icon.svg",
        badge: "icon.svg",
        showTrigger: new TimestampTrigger(ts),
        data: { entryId: entry.id },
      });
    } catch (e) {
      console.warn("Failed to schedule notification", e);
    }
  }

  // Reschedule the full set (called after unlock and after edits).
  async function syncAll(entries) {
    if (!supportsTriggers() || permission() !== "granted") return;
    const reg = await getReg();
    if (reg && reg.getNotifications) {
      try {
        const all = await reg.getNotifications({ includeTriggered: true });
        all.filter((n) => n.tag && n.tag.startsWith(TAG_PREFIX)).forEach((n) => n.close());
      } catch {}
    }
    for (const e of entries) {
      if (e.alertEnabled && e.alertDate) await scheduleForEntry(e);
    }
  }

  return { init, requestPermission, supportsTriggers, permission, scheduleForEntry, cancelForEntry, syncAll };
})();

// --- ES module surface -------------------------------------------------
export const { init, requestPermission, supportsTriggers, permission, scheduleForEntry, cancelForEntry, syncAll } = _mod;
export default _mod;
